import { Box, Tab, Tabs } from "@mui/material";
import { useState, type SyntheticEvent } from "react";
import TabSection from "../../../../common/TabSection";
import FormatableTextBox from "./FormatableTextBox";
import { useField } from "formik";
import { FormikRichTextFieldProps, NarrativeFieldName } from "./types";
import { richTextMaxWords } from "../../../formatting/defaults";

const narrativeFields: Array<{
  name: NarrativeFieldName;
  id: keyof typeof richTextMaxWords;
  label: string;
}> = [
  {
    name: "narrative.project-information",
    id: "project-information",
    label: "Project Information",
  },
  {
    name: "narrative.area-through-time",
    id: "area-through-time",
    label: "Area Through Time",
  },
  {
    name: "narrative.good-for-people",
    id: "good-for-people",
    label: "Good For People",
  },
  {
    name: "narrative.good-for-earth",
    id: "good-for-earth",
    label: "Good For Earth",
  },
];

function tabInfo(index: number) {
  return {
    id: `solution-narrative-form-tab-${index}`,
    "aria-controls": `solution-narrative-tabpanel-${index}`,
  };
}

export function FormikTextField({
  name,
  ...editorProps
}: FormikRichTextFieldProps) {
  const [field, meta, helpers] = useField<string>(name);
  const hasError = meta.touched && Boolean(meta.error);
  return (
    <FormatableTextBox
      {...editorProps}
      initialValue={field.value ?? ""}
      onChange={(value) => void helpers.setValue(value, true)}
      onBlur={(value) => {
        void helpers.setTouched(true, false);
        void helpers.setValue(value, true);
      }}
      error={hasError}
      errorText={hasError ? meta.error : undefined}
    />
  );
}

function NarrativeEditableFields() {
  const [selectedTab, setSelectedTab] = useState(0);
  const handleTabChange = (_event: SyntheticEvent, nextTab: number) =>
    setSelectedTab(nextTab);

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          aria-label="Narrative sections"
          variant="scrollable"
          scrollButtons="auto"
        >
          {narrativeFields.map(({ id, label }, index) => (
            <Tab key={id} label={label} {...tabInfo(index)} />
          ))}
        </Tabs>
      </Box>

      {narrativeFields.map(({ name, id, label }, index) => (
        <TabSection key={id} value={selectedTab} index={index}>
          <FormikTextField
            name={name}
            id={id}
            label={label}
            helperText="Paragraphs, lists and links are supported."
            maxWords={richTextMaxWords[id]}
          />
        </TabSection>
      ))}
    </>
  );
}

export default NarrativeEditableFields;
